// Share-platform registry — single source of truth for every external
// platform `<ShareModal>` knows how to render.
//
// ─────────────────────────────────────────────────────────────────
// HOW TO ADD A NEW PLATFORM (e.g. Bluesky, LinkedIn, Threads)
// ─────────────────────────────────────────────────────────────────
//   1. Add a builder function in `intents.ts` and append the channel
//      name to the `ShareChannel` union there.
//   2. Add an entry below — { icon, brandClass, intentBuilder, labelKey }.
//   3. Add the localized label under `share.modal.<channel>` in `en.json`,
//      then run `pnpm i18n:sync` to propagate to all locales.
//   4. Done. The `unused.ts` i18n script imports `SHARE_PLATFORM_DYNAMIC_KEYS`
//      below — no separate edit to the dynamic-keys allowlist needed.
//
// If the new platform also needs surface-specific analytics mapping,
// also extend `events.ts` (each surface's mapper handles channel actions).
//
// ─────────────────────────────────────────────────────────────────
// TINT STYLE
// ─────────────────────────────────────────────────────────────────
// Soft tinted backgrounds drawn from Morfi semantic tokens, with a
// brand-coloured solid icon on top. Theme-aware in both light and
// dark via the underlying token color-mix.

import {
  NewTwitterIconSolid,
  RedditIconSolid,
  TelegramIconSolid,
  WhatsappIconSolid,
} from '@/components/ui/icon/brands';

import {
  redditIntent,
  type ShareContent,
  telegramIntent,
  whatsappIntent,
  xIntent,
} from './intents';

/** The subset of `ShareChannel` that `<ShareModal>` can render today.
 *  SMS and mailto are intent-buildable (see `intents.ts`) but have no
 *  registry entry yet, so the modal won't surface buttons for them. */
export type PlatformChannel = 'x' | 'telegram' | 'whatsapp' | 'reddit';

export interface PlatformDescriptor {
  /** Icon glyph for the circular share button. */
  icon: typeof NewTwitterIconSolid;
  /** Tailwind classes for the soft tinted circle (bg + icon color +
   *  hover). Built from Morfi semantic tokens so dark mode works. */
  tintClass: string;
  /** Pure intent-URL builder from `intents.ts`. */
  intentBuilder: (content: ShareContent) => string;
  /** i18n key for the button's aria-label (e.g. `'share.modal.x'`). */
  labelKey: string;
  /** Short, brand-name label rendered under the circle (e.g. `'X'`).
   *  Brand names are not translated. */
  name: string;
}

export const PLATFORM_REGISTRY: Record<PlatformChannel, PlatformDescriptor> = {
  x: {
    icon: NewTwitterIconSolid,
    tintClass:
      'bg-background-tertiary text-foreground-primary group-hover:bg-background-tertiary/80',
    intentBuilder: xIntent,
    labelKey: 'share.modal.x',
    name: 'X (Twitter)',
  },
  telegram: {
    icon: TelegramIconSolid,
    tintClass:
      'bg-product-share-telegram/10 text-product-share-telegram group-hover:bg-product-share-telegram/15',
    intentBuilder: telegramIntent,
    labelKey: 'share.modal.telegram',
    name: 'Telegram',
  },
  whatsapp: {
    icon: WhatsappIconSolid,
    tintClass:
      'bg-product-share-whatsapp/10 text-product-share-whatsapp group-hover:bg-product-share-whatsapp/15',
    intentBuilder: whatsappIntent,
    labelKey: 'share.modal.whatsapp',
    name: 'WhatsApp',
  },
  reddit: {
    icon: RedditIconSolid,
    tintClass:
      'bg-product-share-reddit/10 text-product-share-reddit group-hover:bg-product-share-reddit/15',
    intentBuilder: redditIntent,
    labelKey: 'share.modal.reddit',
    name: 'Reddit',
  },
};

/** Default render order for the modal's platform row. */
export const PLATFORM_CHANNELS: readonly PlatformChannel[] = Object.keys(
  PLATFORM_REGISTRY,
) as PlatformChannel[];

/** Type guard — narrows arbitrary input to a known platform channel. */
export function isPlatformChannel(value: string): value is PlatformChannel {
  return value in PLATFORM_REGISTRY;
}

/** Every i18n label key the modal looks up dynamically (`t(channel)`).
 *  Imported by `scripts/i18n/unused.ts` so adding a platform via the
 *  registry auto-registers its label as a dynamic key. */
export const SHARE_PLATFORM_DYNAMIC_KEYS: readonly string[] = PLATFORM_CHANNELS.map(
  (c) => PLATFORM_REGISTRY[c].labelKey,
);
